import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const mainColor = '#ffffff';
const altColor = '#f9f9f9';
const subduedColor = '#a2a2a2';
const darkBlueColor = '#0056b3';
const greenHightlight = '#6786a1';

// Define FirstSection (already in your code)
const FirstSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: mainColor,
                padding: '40px 80px',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    maxWidth: '1200px',
                    margin: '0 auto',
                }}
            >
                {/* Text Block */}
                <Box
                    sx={{
                        flex: 1,
                        textAlign: { xs: 'center', md: 'left' },
                        marginRight: { md: '40px' },
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 500,
                            color: '#777',
                            textAlign: { xs: 'center', md: 'left' },
                            marginBottom: '8px',
                        }}
                    >
                        Instapath Pilot
                    </Typography>

                    <Typography variant="h3" sx={{ fontWeight: 500, marginBottom: '16px' }}>
                        Revolutionizing Drone Mission Management.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#555', marginBottom: '12px' }}>
                        Experience aviation weather tracking, geofence clarity, and streamlined mission planning.
                    </Typography>
                    <Typography variant="body1" sx={{ color: darkBlueColor, marginBottom: '24px' }}>
                        Built for commercial drone pilots—perfect for recreational flyers too!
                    </Typography>
                    <Button
                        component="a"
                        href="https://apps.apple.com/us/app/instapath-pilot/id6630364972"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: 'transparent' },
                            marginTop: '44px',
                            mx: 'auto',
                        }}
                    >
                        <img
                            src="/Download_on_the_App_Store_Badge_US-UK_blk_092917.png"
                            alt="Download on the App Store"
                            style={{ width: '180px' }}
                        />
                    </Button>
                </Box>

                {/* Image */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        maxWidth: '270px',
                    }}
                >
                    <img
                        src="/mockup-iphone16-weather-home.png"
                        alt="App Mockup"
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '16px',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

// Define SecondSection
const SecondSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: altColor,
                borderTop: '1px solid #ddd',
                padding: '40px 80px',
                textAlign: 'center',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '1200px',
                    margin: '0 auto',
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 500, marginBottom: '16px' }}>
                    Your Missions, Backed Up and Ready Everywhere.
                </Typography>
                <Typography variant="body1" sx={{ color: '#555', marginBottom: '32px' }}>
                    Seamlessly sync your mission logs with Instapath Cloud for secure backups, instant access across devices,
                    and effortless collaboration.
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        marginBottom: '16px',
                    }}
                >
                    <Box
                        component="img"
                        src="/mockup-iphone16-mission-list.png"
                        alt="Mission List"
                        sx={{
                            maxWidth: '270px',
                            width: '100%',
                            height: 'auto',
                        }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src="/instapath-cloud.png"
                            alt="Instapath Cloud"
                            sx={{
                                maxWidth: '100px',
                                height: 'auto',
                                marginBottom: '8px',
                            }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#555', marginBottom: '8px' }}>
                            Instapath Cloud
                        </Typography>
                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    flex: 1,
                                    borderBottom: '2px solid #ddd',
                                    minWidth: '100px', // Minimum width for the horizontal line
                                }}
                            />
                            <Box sx={{ width: '100px' }} />
                            <Box
                                sx={{
                                    flex: 1,
                                    borderBottom: '2px solid #ddd',
                                    minWidth: '100px', // Minimum width for the horizontal line
                                }}
                            />
                        </Box>
                    </Box>
                    <Box
                        component="img"
                        src="/mockup-iphone16-sync.png"
                        alt="Sync"
                        sx={{
                            maxWidth: '270px',
                            width: '100%',
                            height: 'auto',
                        }}
                    />
                </Box>
            </Box>

            {/* Bottom Text Section */}
            <Box
                sx={{
                    marginTop: '40px',
                    maxWidth: '900px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontStyle: 'italic',
                        color: darkBlueColor,
                        fontWeight: '500',
                        marginBottom: '16px',
                        textAlign: 'left',
                        fontSize: '1.1rem',
                    }}
                >
                    —It’s not just data.
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        lineHeight: 1.8,
                        textAlign: 'left'
                    }}
                >
                    For commercial drone pilots, mission data isn’t just numbers—it’s the backbone of their operations.
                    Instapath Cloud ensures your critical mission logs are securely backed up, safeguarding your business
                    from unexpected data loss due to device failures or errors. Rest easy knowing your data is encrypted
                    and stored in a secure cloud environment designed for aviation professionals.
                </Typography>
            </Box>
        </Box>
    );
};

const ThirdSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: mainColor,
                borderTop: '1px solid #ddd',
                padding: '40px 80px',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    gap: 4, // Spacing between the image and text on smaller screens
                }}
            >
                {/* Left Image */}
                <Box
                    sx={{
                        flex: 1,
                        maxWidth: '270px',
                        width: '100%',
                        textAlign: 'center',
                        marginBottom: { xs: '20px', md: '0' }, // Spacing on smaller screens
                    }}
                >
                    <img
                        src="/mockup-iphone16-geofenced-donotfly.png"
                        alt="Geofence Map"
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '16px',
                        }}
                    />
                </Box>

                {/* Right Text */}
                <Box
                    sx={{
                        flex: 2,
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 500,
                            marginBottom: '16px',
                        }}
                    >
                        Stay Compliant with Map Geofence Layers.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: subduedColor,
                            marginBottom: '24px',
                            lineHeight: 1.8,
                        }}
                    >
                        Visualize restricted areas, plan smarter routes, and ensure regulatory compliance with precision
                        geofencing tailored for Part 107 pilots!
                    </Typography>

                    {/* Bullet List */}
                    <Box component="ul" sx={{ paddingLeft: { xs: 2, md: 3 }, color: '#555', lineHeight: 1.8 }}>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>
                                Clear Visualization of No-Fly Zones:
                            </strong>{' '}
                            Access up-to-date geofence data for restricted areas such as national parks and wildlife areas.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Smarter Flight Planning:</strong> Plan
                            routes that respect geofence boundaries, reducing the risk of operational delays or
                            violations. Save time and ensure safer, compliant operations.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>
                                Integrated Compliance Assistance:
                            </strong>{' '}
                            Automatically identify safe areas to fly and receive visual alerts when planning near
                            restricted zones, helping you adhere to FAA Part 107 regulations effortlessly.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>
                                Enhanced Situational Awareness:
                            </strong>{' '}
                            Gain a real-time understanding of your flight environment with layered geofence maps,
                            empowering you to make confident decisions during mission planning and execution.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Peace of Mind for Your Business:</strong>{' '}
                            Minimize liability and maintain your professional reputation by consistently operating within
                            regulations.
                        </Typography>
                    </Box>

                    {/* Footer Text */}
                    <Typography
                        variant="body1"
                        sx={{
                            fontStyle: 'italic',
                            color: darkBlueColor,
                            fontWeight: 500,
                            marginTop: '32px',
                        }}
                    >
                        —We’re rolling out more layers soon!
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const FourthSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: altColor,
                borderTop: '1px solid #ddd',
                padding: '40px 80px',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', md: 'row-reverse' }, // Reverse layout for image on the right
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    gap: 4, // Spacing between the text and image
                }}
            >
                {/* Right Image */}
                <Box
                    sx={{
                        flex: 1,
                        maxWidth: '270px',
                        width: '100%',
                        textAlign: 'center',
                        marginBottom: { xs: '20px', md: '0' }, // Spacing for smaller screens
                    }}
                >
                    <img
                        src="/mockup-iphone16-weather-home.png"
                        alt="Aviation Weather Insights"
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '16px',
                        }}
                    />
                </Box>

                {/* Left Text */}
                <Box
                    sx={{
                        flex: 2,
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 500,
                            marginBottom: '16px',
                        }}
                    >
                        Live Aviation Weather at Your Fingertips.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: subduedColor,
                            marginBottom: '24px',
                            lineHeight: 1.8,
                        }}
                    >
                        Real-time weather insights tailored for drone pilots—featuring ECAM-style CLEAR TO FLY statuses,
                        FAA compliance checks, and interactive wind guidance for safe and informed flights.
                    </Typography>

                    {/* Bullet List */}
                    <Box component="ul" sx={{ paddingLeft: { xs: 2, md: 3 }, color: '#555', lineHeight: 1.8 }}>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>
                                Clear and Actionable Insights:
                            </strong>{' '}
                            The ECAM-style readout gives a CLEAR TO FLY or DO NOT FLY status, simplifying decision-making for drone pilots.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>FAA Compliance Built In:</strong>{' '}
                            Automatically checks weather against FAA Part 107 and TRUST guidelines, including visibility
                            (3+ miles), wind speed thresholds, and strobe light requirements, ensuring you’re always operating within the rules.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Actionable Alerts:</strong>{' '}
                            For DO NOT FLY, the ECAM readout highlights specific reasons like PRECIP PROB, WIND, GUSTS
                            or TEMP, helping you understand and plan better.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Dynamic Wind Gauge:</strong>{' '}
                            A live wind direction gauge syncs with your device’s compass, making it easy to understand
                            wind orientation and adjust your flight path.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const FifthSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: mainColor,
                borderTop: '1px solid #ddd',
                padding: '40px 80px',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    gap: 4, // Spacing between the image and text on smaller screens
                }}
            >
                {/* Left Image */}
                <Box
                    sx={{
                        flex: 1,
                        maxWidth: '270px',
                        width: '100%',
                        textAlign: 'center',
                        marginBottom: { xs: '20px', md: '0' }, // Spacing on smaller screens
                    }}
                >
                    <img
                        src="/mockup-iphone16-mission-enhanced.png"
                        alt="Enhanced Mission Planning"
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '16px',
                        }}
                    />
                </Box>

                {/* Right Text */}
                <Box
                    sx={{
                        flex: 2,
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 500,
                            marginBottom: '16px',
                        }}
                    >
                        Advanced Mission Planning.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#555',
                            marginBottom: '24px',
                            lineHeight: 1.8,
                        }}
                    >
                        Why Every Drone Pilot Needs a Mission Log.
                    </Typography>

                    {/* Bullet List */}
                    <Box component="ul" sx={{ paddingLeft: { xs: 2, md: 3 }, color: '#555', lineHeight: 1.8 }}>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>
                                Insurance and Liability:
                            </strong>{' '}
                            Records are essential for insurance claims or legal defense, should any incidents occur during
                            or after the mission.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Operational Insights:</strong> Mission
                            logs aid in planning, maintenance scheduling, and resource allocation.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Future Reference:</strong> Past missions
                            can provide insights for similar future operations and allow operators to understand previous
                            conditions and outcomes.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>
                                Compliance and Legal Requirements:
                            </strong>{' '}
                            Many regulatory bodies require that flight records be retained for a certain period. Deleting
                            these records could put you at risk of non-compliance with legal requirements.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Audit and Accountability:</strong>{' '}
                            Retaining completed mission logs ensures there is an audit trail for review and accountability.
                        </Typography>
                        <Typography component="li" sx={{ marginBottom: '16px' }}>
                            <strong style={{ color: greenHightlight, fontWeight: 500 }}>Historical Data for Analysis:</strong>{' '}
                            Completed mission data is valuable for performance analysis, trend identification, and
                            operational improvements.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// Combine Sections into MobilePlatform Page
const MobilePlatform = () => {
    return (
        <Box>
            <div style={{ width: '100%', margin: 0 }}>
                <FirstSection />
            </div>
            <div style={{ width: '100%', margin: 0 }}>
                <SecondSection />
            </div>
            <div style={{ width: '100%', margin: 0 }}>
                <ThirdSection />
            </div>
            <div style={{ width: '100%', margin: 0 }}>
                <FourthSection />
            </div>
            <div style={{ width: '100%', margin: 0 }}>
                <FifthSection />
            </div>
        </Box>
    );
};

export default MobilePlatform;